import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import PlanService from '@services/api/plan.service';
import { logger } from '@utils/logger';
import { shouldRetryApiCall, resetApiRetry } from '@utils/apiRetry';

const initialState = {
  subscription: null,
  currentPlan: null,
  usageStats: null,
  loading: false,
  error: null,
  upgradePlans: null,
  subscriptionError: null,
  lastFetchAttempt: null,
  fetchAttemptCount: 0,
};

export const fetchCurrentSubscription = createAsyncThunk(
  'subscription/fetchCurrent',
  async (_, { rejectWithValue }) => {
    const apiKey = 'getCurrentPlan';
    
    if (!shouldRetryApiCall(apiKey)) {
      return rejectWithValue({
        error: 'MAX_RETRIES_EXCEEDED',
        message: 'Maximum retry attempts reached. Please try again later.',
      });
    }

    try {
      const planResponse = await PlanService.getCurrentPlan();
      
      resetApiRetry(apiKey);

      if (planResponse.success && planResponse.data) {
        return {
          subscription: planResponse.data.subscription,
          currentPlan: planResponse.data.plan,
          usageStats: planResponse.data.usage
        };
      }

      throw new Error('Failed to fetch subscription');
    } catch (error) {
      logger.error('Failed to fetch subscription', error);
      
      const errorStatus = error.response?.status;
      
      // Handle 500+ Server Errors - block retries immediately
      if (errorStatus >= 500) {
        shouldRetryApiCall(apiKey, errorStatus);
        return rejectWithValue({
          error: 'SERVER_ERROR',
          message: 'Server error. Please try again later.',
          statusCode: errorStatus
        });
      }
      
      // Handle 429 Too Many Requests
      if (errorStatus === 429) {
        shouldRetryApiCall(apiKey, errorStatus);
        return rejectWithValue({
          error: 'RATE_LIMIT_EXCEEDED',
          message: 'Too many requests. Please wait a moment.',
          statusCode: 429
        });
      }
      
      // Handle network errors (backend down)
      if (!error.response) {
        shouldRetryApiCall(apiKey, 0);
        return rejectWithValue({
          error: 'NETWORK_ERROR',
          message: 'Cannot connect to server. Please check your connection.',
          statusCode: 0
        });
      }
      
      // Handle 403 Forbidden - user is not a student (admin/mentor)
      if (errorStatus === 403) {
        resetApiRetry(apiKey);
        return rejectWithValue({
          error: 'ACCESS_FORBIDDEN',
          message: 'Plan system is only available for student accounts',
          statusCode: 403
        });
      }
      
      // Handle subscription errors with upgrade plans
      const errorCode = error.response?.data?.error;
      if (errorCode === 'SUBSCRIPTION_EXPIRED' || errorCode === 'NO_ACTIVE_SUBSCRIPTION') {
        resetApiRetry(apiKey);
        return rejectWithValue({
          error: errorCode,
          message: error.response?.data?.message || 'Subscription error',
          upgradePlans: error.response?.data?.data?.upgradePlans || error.response?.data?.data?.availablePlans || [],
          expiredPlan: error.response?.data?.data?.expiredPlan,
          expiredAt: error.response?.data?.data?.expiredAt
        });
      }
      
      shouldRetryApiCall(apiKey, errorStatus);
      return rejectWithValue({
        error: 'FETCH_ERROR',
        message: error.response?.data?.message || error.message || 'Failed to fetch subscription',
        statusCode: errorStatus
      });
    }
  }
);

export const activateSubscription = createAsyncThunk(
  'subscription/activate',
  async (planId, { rejectWithValue }) => {
    try {
      const response = await PlanService.activatePlan(planId);
      
      if (response.success) {
        return response.data;
      }
      
      throw new Error('Failed to activate plan');
    } catch (error) {
      logger.error('Failed to activate plan', error);
      return rejectWithValue(error.message || 'Failed to activate plan');
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearSubscription: (state) => {
      state.subscription = null;
      state.currentPlan = null;
      state.usageStats = null;
      state.error = null;
      state.fetchAttemptCount = 0;
      state.lastFetchAttempt = null;
    },
    clearSubscriptionError: (state) => {
      const fatalErrors = ['NETWORK_ERROR', 'SERVER_ERROR', 'MAX_RETRIES_EXCEEDED'];
      if (!state.error || !fatalErrors.includes(state.error.error)) {
        state.error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.loading = true;
        state.lastFetchAttempt = Date.now();
        state.fetchAttemptCount += 1;
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload.subscription;
        state.currentPlan = action.payload.currentPlan;
        state.usageStats = action.payload.usageStats;
        state.error = null;
        state.fetchAttemptCount = 0;
        logger.info('Subscription fetched successfully');
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.subscriptionError = action.payload?.error;
        state.upgradePlans = action.payload?.upgradePlans || null;
        
        const errorType = action.payload?.error;
        if (['NETWORK_ERROR', 'SERVER_ERROR'].includes(errorType)) {
          logger.error(`Fatal error fetching subscription: ${errorType}. API calls blocked.`, action.payload);
        } else {
          logger.error('Failed to fetch subscription', action.payload);
        }
      })
      .addCase(activateSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPlan = action.payload.plan;
        state.error = null;
        logger.info('Plan activated successfully');
      })
      .addCase(activateSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        logger.error('Failed to activate plan', action.payload);
      });
  },
});

export const { clearSubscription, clearSubscriptionError } = subscriptionSlice.actions;

export const selectSubscription = (state) => state.subscription;
export const selectSubscriptionData = (state) => state.subscription.subscription;
export const selectCurrentPlan = (state) => state.subscription.currentPlan;
export const selectUsageStats = (state) => state.subscription.usageStats;
export const selectSubscriptionLoading = (state) => state.subscription.loading;
export const selectSubscriptionError = (state) => state.subscription.error;
export const selectUpgradePlans = (state) => state.subscription.upgradePlans;
export const selectSubscriptionErrorCode = (state) => state.subscription.subscriptionError;

export default subscriptionSlice.reducer;
